import type { ReportColumn, ReportColumnKind, ReportColumnTotal, ReportExportFormat } from './types';

/**
 * The export contract.
 *
 * The client does NOT send rows. It sends the same filter the screen used plus a description of the
 * columns it is showing, and the server re-runs the query and renders the document. That is the
 * whole point: an export produced from the screen's own paged/filtered client state is a different
 * report from the one on screen, and the two silently disagree the moment either changes. The
 * server owns the figures; the client owns what the columns are called and what language they are
 * called it in.
 */

export interface ReportExportColumn {
  key: string;
  /** Localized header text, exactly as it reads on screen. */
  header: string;
  kind: ReportColumnKind;
  total?: ReportColumnTotal;
}

/**
 * Chrome strings the server prints around the table.
 * `period` and `scope` are the RENDERED values ("1-31 Aug 2026", "All departments"): the client
 * formats them in its own locale. `generatedBy` / `generatedAt` are label WORDS; the server appends
 * the user name and the company-local timestamp. The rest are label words too.
 */
export interface ReportExportLabels {
  /** Rendered period text, e.g. from formatPeriod(). */
  period: string;
  /** Rendered scope text, e.g. "All departments" or "Finance, Ops". */
  scope: string;
  generatedBy: string;
  generatedAt: string;
  subtotal: string;
  total: string;
  page: string;
  of: string;
  groupSummary?: string;
}

export interface ReportExportRequest<TInput> {
  /** The same filter object the screen queried with. */
  input: TInput;
  format: ReportExportFormat;
  /** `i18n.language` — 'en' | 'ar' | 'fr'. */
  culture: string;
  rtl: boolean;
  /** Localized report title; becomes the sheet name and the document heading. */
  title: string;
  columns: ReportExportColumn[];
  labels: ReportExportLabels;
  /**
   * Optional per-column value -> localized label maps (column key -> raw server value -> label), for
   * status/enum columns so the file reads in the user's language.
   */
  lookups?: Record<string, Record<string, string>>;
}

/** The standard label set, so every report prints the same words around its table. */
export function reportExportLabels(
  t: (key: string, options?: Record<string, unknown>) => string,
): ReportExportLabels {
  return {
    period: t('reports.period'),
    scope: t('reports.scope'),
    generatedBy: t('reports.generatedBy'),
    generatedAt: t('reports.generatedAt'),
    subtotal: t('reports.subtotal'),
    total: t('reports.total'),
    page: t('reports.page'),
    of: t('reports.of'),
    groupSummary: t('reports.groupSummary'),
  };
}

export interface BuildExportRequestParams<TInput, Row> {
  input: TInput;
  format: ReportExportFormat;
  title: string;
  columns: ReportColumn<Row>[];
  culture: string;
  rtl: boolean;
  labels: ReportExportLabels;
  lookups?: Record<string, Record<string, string>>;
}

/**
 * Turns the columns the table is rendering into the columns the document should carry.
 *
 * `custom` is a client-only kind (a chip, a link); it is sent as `text` so the server writes the
 * raw value instead of dropping the column. Money totals default to `sum` here for the same reason
 * they do in `computeTotals`, so the footer in the file matches the footer on screen.
 */
export function buildExportRequest<TInput, Row>({
  input,
  format,
  title,
  columns,
  culture,
  rtl,
  labels,
  lookups,
}: BuildExportRequestParams<TInput, Row>): ReportExportRequest<TInput> {
  return {
    input,
    format,
    culture,
    rtl,
    title,
    labels,
    ...(lookups ? { lookups } : {}),
    columns: columns.map((column) => ({
      key: column.key,
      header: column.label,
      kind: column.kind === 'custom' ? 'text' : column.kind,
      total: column.total ?? (column.kind === 'money' ? 'sum' : 'none'),
    })),
  };
}

/** `Attendance & Overtime - Aug 2026.xlsx`, with the characters Windows refuses stripped out. */
export function reportExportFilename(
  title: string,
  periodText: string,
  format: ReportExportFormat,
): string {
  const base = [title, periodText].filter(Boolean).join(' - ');
  const safe = base.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  return `${safe || 'report'}.${format}`;
}
